import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  WaterDrop,
  Bolt,
  Restore,
  CheckCircleOutline,
} from "@mui/icons-material";
import { CONFIG } from "../../config";
import { getHeaders } from "../../API/getHeaders";

async function patchResidenceThresholds(residenceId, payload) {
  const headers = await getHeaders();
  const res = await fetch(`${CONFIG.API_URL}/residences/${residenceId}/thresholds`, {
    method: "PATCH",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Erreur lors de la sauvegarde");
  return res.json();
}

function ThresholdRow({ icon, color, label, unit, yellowValue, redValue, isCustom, onSave, onReset, isMobile }) {
  const [yellow, setYellow] = useState(yellowValue ?? "");
  const [red, setRed] = useState(redValue ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const isDirty = Number(yellow) !== yellowValue || Number(red) !== redValue;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(Number(yellow) || null, Number(red) || null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await onReset();
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      backgroundColor: "var(--bg-card)",
      "& fieldset": { borderColor: "var(--border-medium)" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
        {icon}
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color }}>
          {label}
        </Typography>
        <Chip
          size="small"
          label={isCustom ? "Personnalisé" : "Défaut établissement"}
          sx={{
            ml: 0.5,
            height: 20,
            fontSize: "0.7rem",
            backgroundColor: isCustom
              ? "var(--color-primary-lighter)"
              : "var(--bg-hover, rgba(255,255,255,0.06))",
            color: isCustom ? "var(--color-primary)" : "var(--text-secondary)",
          }}
        />
      </Box>

      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={6} sm={4}>
          <TextField
            label="Seuil jaune"
            type="number"
            value={yellow}
            onChange={(e) => setYellow(e.target.value)}
            size="small"
            fullWidth
            sx={{ ...fieldSx, "& .MuiOutlinedInput-root fieldset": { borderColor: "#fedd37" } }}
            InputProps={{
              endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: "0.75rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{unit}</Typography></InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            label="Seuil rouge"
            type="number"
            value={red}
            onChange={(e) => setRed(e.target.value)}
            size="small"
            fullWidth
            sx={{ ...fieldSx, "& .MuiOutlinedInput-root fieldset": { borderColor: "#fe3e26" } }}
            InputProps={{
              endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: "0.75rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{unit}</Typography></InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving || !isDirty}
              fullWidth
              sx={{
                fontSize: "0.75rem",
                backgroundColor: saved ? "var(--color-primary)" : undefined,
              }}
            >
              {saving ? <CircularProgress size={14} /> : saved ? <CheckCircleOutline fontSize="small" /> : "Sauvegarder"}
            </Button>
            {isCustom && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Restore fontSize="small" />}
                onClick={handleReset}
                disabled={saving}
                sx={{ fontSize: "0.75rem", borderColor: "var(--border-medium)", whiteSpace: "nowrap" }}
              >
                Réinit.
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function AlertThresholdsSection({ residence, establishment, residenceId, hasWater, hasElec, isMobile }) {
  const effectiveWaterYellow =
    residence?.yellow_water_limit_per_person ?? establishment?.yellow_water_limit_per_person ?? null;
  const effectiveWaterRed =
    residence?.red_water_limit_per_person ?? establishment?.red_water_limit_per_person ?? null;
  const effectiveElecYellow =
    residence?.yellow_elec_limit_per_person ?? establishment?.yellow_elec_limit_per_person ?? null;
  const effectiveElecRed =
    residence?.red_elec_limit_per_person ?? establishment?.red_elec_limit_per_person ?? null;

  const isWaterCustom = !!residence?.yellow_water_limit_per_person || !!residence?.red_water_limit_per_person;
  const isElecCustom = !!residence?.yellow_elec_limit_per_person || !!residence?.red_elec_limit_per_person;

  const handleSaveWater = useCallback(async (yellow, red) => {
    await patchResidenceThresholds(residenceId, {
      yellow_water_limit_per_person: yellow,
      red_water_limit_per_person: red,
    });
  }, [residenceId]);

  const handleResetWater = useCallback(async () => {
    await patchResidenceThresholds(residenceId, {
      yellow_water_limit_per_person: null,
      red_water_limit_per_person: null,
    });
  }, [residenceId]);

  const handleSaveElec = useCallback(async (yellow, red) => {
    await patchResidenceThresholds(residenceId, {
      yellow_elec_limit_per_person: yellow,
      red_elec_limit_per_person: red,
    });
  }, [residenceId]);

  const handleResetElec = useCallback(async () => {
    await patchResidenceThresholds(residenceId, {
      yellow_elec_limit_per_person: null,
      red_elec_limit_per_person: null,
    });
  }, [residenceId]);

  if (!hasWater && !hasElec) return null;

  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: "6px !important",
            border: "1px solid var(--border-light)",
            boxShadow: "var(--shadow-sm)",
            backgroundColor: "var(--bg-card)",
          }}
        >
          <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
        <Typography sx={{
          fontFamily: "var(--font-display)",
          fontSize: isMobile ? "1rem" : "1.125rem",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--text-primary)",
          mb: 2,
        }}>
          Seuils de consommation
        </Typography>

        {hasWater && (
          <ThresholdRow
            icon={<WaterDrop sx={{ fontSize: 18, color: "var(--color-water)" }} />}
            color="var(--color-water)"
            label="Eau"
            unit="L/pers"
            yellowValue={effectiveWaterYellow}
            redValue={effectiveWaterRed}
            isCustom={isWaterCustom}
            onSave={handleSaveWater}
            onReset={handleResetWater}
            isMobile={isMobile}
          />
        )}

        {hasWater && hasElec && <Divider sx={{ my: 1.5 }} />}

        {hasElec && (
          <ThresholdRow
            icon={<Bolt sx={{ fontSize: 18, color: "var(--color-electricity)" }} />}
            color="var(--color-electricity)"
            label="Électricité"
            unit="kWh/pers"
            yellowValue={effectiveElecYellow}
            redValue={effectiveElecRed}
            isCustom={isElecCustom}
            onSave={handleSaveElec}
            onReset={handleResetElec}
            isMobile={isMobile}
          />
        )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default AlertThresholdsSection;
